import { useState, useMemo, useEffect, useRef } from 'react';
import { Grid, Chip, Typography, Button } from '@mui/material';
import { debounce, isFunction, isArray, round, isEmpty, isEqual } from 'lodash';
import { red, green } from '@mui/material/colors';

export default function DefectCheckField({ initial, onChange, config, debounceMs = 1000, editable }) {
  const [values, setValues] = useState([]);
  const resultTimer = useRef(null);
  const lastResults = useRef([]);

  const { ratio, passing } = useMemo(() => {
    if (isEmpty(values)) {
      return { ratio: null, passing: null };
    }
    const ratio = round(values.filter((x) => x === 'ok').length / values.length, 2);
    const passing = ratio >= config.acceptableRatio;

    return {
      ratio,
      passing,
    };
  }, [values, config]);

  useEffect(() => {
    // mitigate side effect of debouncing
    if (initial?.data && lastResults.current.some((x) => isEqual(x, initial))) {
      return;
    }
    if (!isArray(initial?.data)) {
      setValues([]);
      return;
    }
    setValues(initial.data);
  }, [initial]);

  function saveValues(data) {
    setValues(data);

    if (resultTimer.current != null) {
      clearTimeout(resultTimer.current);
    }

    resultTimer.current = setTimeout(() => sendResult({ data }), debounceMs);
  }

  const sendResult = (res) => {
    if (!isFunction(onChange)) return;

    // store the last 3 sent results
    const resultsToStore = 10;
    lastResults.current.push(res);
    lastResults.current = lastResults.current.slice(-resultsToStore);

    onChange(res);
  };

  return (
    <Grid container alignItems={'center'}>
      {config && (
        <Grid container alignItems={'center'}>
          <Typography sx={{ color: (theme) => theme.palette.text.secondary }}>
            Target: {config.acceptableRatio * 100}%
          </Typography>
        </Grid>
      )}

      <Grid container alignItems={'center'} sx={{ mt: 1 }} spacing={1}>
        {isArray(config.options) &&
          ['ok', ...config.options].map((value, index) => (
            <Grid item key={index}>
              <Button
                onClick={() => saveValues([...values, value])}
                variant="outlined"
                sx={{ borderRadius: 50, borderWidth: 2, textTransform: 'none' }}
              >
                {value}
              </Button>
            </Grid>
          ))}
      </Grid>

      <Grid container alignItems={'center'} sx={{ mt: 1 }} spacing={1}>
        {values.map((value, index) => (
          <Grid item key={index}>
            <Chip label={value} color="primary" onDelete={() => saveValues(values.filter((_x, i) => i !== index))} />
          </Grid>
        ))}
      </Grid>

      <Grid container alignItems={'center'} sx={{ mt: 3 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
          Result:
        </Typography>
      </Grid>

      <Grid container alignItems={'center'} sx={{ mt: 1 }}>
        <Chip sx={{ mr: 1 }} label={`ok: ${ratio != null ? `${round(ratio * 100, 0)}%` : '-'}`} />
        {passing != null &&
          (passing ? (
            <Chip sx={{ background: green[500] }} label={'pass'} />
          ) : (
            <Chip sx={{ background: red[500] }} label="fail" />
          ))}
      </Grid>
    </Grid>
  );
}
